/**
 * SocialScheduleView Component
 *
 * Calendar view of scheduled posts with month navigation.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Facebook,
  Instagram,
  Music2,
  Clock,
  ArrowLeft,
} from 'lucide-react';

import { ScheduleCalendarSkeleton } from './SocialPostSkeleton';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { social } from '@/services/api/admin';

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
};

const PLATFORM_COLORS = {
  facebook: 'bg-blue-500',
  instagram: 'bg-pink-500',
  tiktok: 'bg-gray-800 dark:bg-white',
};

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SocialScheduleView = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get month/year for display
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Calculate calendar grid
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        date: new Date(currentYear, currentMonth, day),
      });
    }

    return days;
  }, [currentMonth, currentYear]);

  // Group posts by date
  const postsByDate = useMemo(() => {
    const grouped = {};
    scheduledPosts.forEach((post) => {
      if (post.scheduled_for) {
        const date = new Date(post.scheduled_for);
        const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(post);
      }
    });
    return grouped;
  }, [scheduledPosts]);

  // Fetch scheduled posts
  useEffect(() => {
    const fetchScheduledPosts = async () => {
      setIsLoading(true);
      try {
        // Get posts for the entire month
        const startDate = new Date(currentYear, currentMonth, 1);
        const endDate = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);

        const data = await social.listPosts({
          status: 'scheduled',
          scheduled_after: startDate.toISOString(),
          scheduled_before: endDate.toISOString(),
          page_size: 100,
        });

        setScheduledPosts(data.posts || []);
      } catch (err) {
        toast({
          title: 'Error',
          description: err.message || 'Failed to load scheduled posts',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchScheduledPosts();
  }, [currentMonth, currentYear, toast]);

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentYear, currentMonth + direction, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getPostsForDate = (date) => {
    if (!date) return [];
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return postsByDate[key] || [];
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <ScheduleCalendarSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/outreach/social')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Posts
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Schedule</h2>
            <p className="text-gray-500 dark:text-gray-400">
              View and manage scheduled posts
            </p>
          </div>
          <Button onClick={() => navigate('/admin/outreach/social/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {MONTHS[currentMonth]} {currentYear}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth(-1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth(1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS_OF_WEEK.map((day) => (
              <div
                key={day}
                className="py-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map(({ day, date }, index) => {
              const posts = getPostsForDate(date);
              const hasMultiplePosts = posts.length > 2;

              return (
                <div
                  key={index}
                  className={cn(
                    'min-h-[100px] p-1 rounded-lg border transition-colors',
                    day === null
                      ? 'bg-gray-50 dark:bg-gray-900 border-transparent'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                    isToday(date) && 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900'
                  )}
                >
                  {day !== null && (
                    <>
                      {/* Day number */}
                      <div
                        className={cn(
                          'text-sm font-medium mb-1',
                          isToday(date)
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-900 dark:text-white'
                        )}
                      >
                        {day}
                      </div>

                      {/* Posts */}
                      <div className="space-y-1">
                        {posts.slice(0, 2).map((post) => (
                          <TooltipProvider key={post.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() =>
                                    navigate(`/admin/outreach/social/${post.id}`)
                                  }
                                  className={cn(
                                    'w-full text-left p-1 rounded text-xs truncate',
                                    'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                                    'hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors'
                                  )}
                                >
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">
                                      {formatTime(post.scheduled_for)}
                                    </span>
                                  </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="right"
                                className="max-w-xs"
                              >
                                <div className="space-y-2">
                                  <p className="font-medium">{post.title}</p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {post.caption}
                                  </p>
                                  <div className="flex items-center gap-1">
                                    {post.target_platforms?.map((platform) => {
                                      const Icon = PLATFORM_ICONS[platform];
                                      return Icon ? (
                                        <div
                                          key={platform}
                                          className={cn(
                                            'p-1 rounded',
                                            PLATFORM_COLORS[platform],
                                            'text-white'
                                          )}
                                        >
                                          <Icon className="h-3 w-3" />
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}

                        {/* Show "+X more" if there are more posts */}
                        {hasMultiplePosts && (
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/outreach/social?tab=scheduled&date=${date.toISOString().split('T')[0]}`
                              )
                            }
                            className="w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            +{posts.length - 2} more
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">This Month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {scheduledPosts.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Scheduled Posts
              </p>
            </div>
            {Object.entries(PLATFORM_ICONS).map(([platform, Icon]) => {
              const count = scheduledPosts.filter((p) =>
                p.target_platforms?.includes(platform)
              ).length;
              return (
                <div
                  key={platform}
                  className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Icon className="h-5 w-5" />
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {count}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                    {platform}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialScheduleView;
